import { isAlreadySubscribed, useStartCheckout } from "@/components/billing/useBilling.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import axios, { apiErrorReason } from "@/lib/http.ts";
import { formatResetMoment } from "@/components/billing/planCopy.ts";
import {
  ALLOWANCE_PERIODS,
  paidTiersAbove,
  PLAN_TIER_LABELS,
  PLAN_TIERS,
  UNITS_EXHAUSTED_REASON,
  type AllowancePeriod,
  type PaidTier,
  type PlanTier,
} from "@/types/entitlements.ts";
import { settingsTabPath } from "@/views/Settings/settingsNav.ts";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

/**
 * What the upgrade flow renders, parsed off the 402: whose plan ran out, over
 * what period, and when it comes back. No unit counts — users are only ever
 * shown sizes and percents.
 */
export type UnitsExhaustedNotice = {
  tier: PlanTier;
  /** ISO instant the allowance resets — exclusive end of the current period. */
  resetsAt: string;
  allowancePeriod: AllowancePeriod;
};

function isPlanTier(value: unknown): value is PlanTier {
  return PLAN_TIERS.some((tier) => tier === value);
}

/** Whether `value` is one of `ALLOWANCE_PERIODS`. */
function isAllowancePeriod(value: unknown): value is AllowancePeriod {
  return ALLOWANCE_PERIODS.some((period) => period === value);
}

/** A string field off an untrusted body, or undefined. */
function stringField(data: object, key: string): string | undefined {
  const value: unknown = Reflect.get(data, key);
  return typeof value === "string" ? value : undefined;
}

/**
 * The exhaustion notice a failed request carries, if that is what it is.
 * Discriminates on the shared `reason` constant, then trusts individual fields
 * only as far as their shape checks out — a missing or malformed field falls
 * back (tier to `free`, period to `month`, reset to `periodEnd`) rather than
 * letting it suppress the whole flow. The fallbacks also cover a deploy-window
 * backend whose body predates `resetsAt` and `allowancePeriod`.
 */
export function parseUnitsExhaustedError(error: unknown): UnitsExhaustedNotice | undefined {
  if (apiErrorReason(error) !== UNITS_EXHAUSTED_REASON) return undefined;
  if (!axios.isAxiosError(error)) return undefined;
  const data: unknown = error.response?.data;
  if (typeof data !== "object" || data === null) return undefined;
  return {
    tier: "tier" in data && isPlanTier(data.tier) ? data.tier : "free",
    resetsAt: stringField(data, "resetsAt") ?? stringField(data, "periodEnd") ?? "",
    allowancePeriod:
      "allowancePeriod" in data && isAllowancePeriod(data.allowancePeriod)
        ? data.allowancePeriod
        : "month",
  };
}

function describe(notice: UnitsExhaustedNotice, canUpgrade: boolean): string {
  const plan = `${PLAN_TIER_LABELS[notice.tier]} plan`;
  const consumed = `You've used all of the AI usage included in your ${plan} this ${notice.allowancePeriod}`;
  const resetsAt = new Date(notice.resetsAt);
  const reset = Number.isNaN(resetsAt.getTime())
    ? ""
    : `, so Okthx can't take on new work until ${formatResetMoment(notice.resetsAt)}`;
  const upgrade = canUpgrade
    ? " Upgrade to keep going now — anything you've already asked for picks up right where it left off."
    : " Anything you've already asked for picks up right where it left off.";
  return `${consumed}${reset}.${upgrade}`;
}

/**
 * The app-wide upgrade flow for the one refusal enforcement can issue.
 *
 * Listens at the axios layer rather than in any caller: every surface that
 * hits the typed 402 — the chat composer today, anything added later — opens
 * this dialog without wiring, and no caller's generic error handling can
 * swallow the refusal into a "something went wrong" toast. The rejection
 * still propagates to the caller, whose own affordance (e.g. the composer's
 * inline notice) remains the anchor after the dialog is dismissed.
 *
 * Renders entirely from the 402 body: what ran out, how much of it, and when
 * it resets. A free-tier user gets direct checkout; a paid tier routes to the
 * Billing panel, where plan changes carry their proration confirmation.
 */
export const UnitsExhaustedDialog = () => {
  const [notice, setNotice] = useState<UnitsExhaustedNotice | null>(null);
  const [, navigate] = useLocation();
  const startCheckout = useStartCheckout();

  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (error: unknown) => {
      const parsed = parseUnitsExhaustedError(error);
      if (parsed) setNotice(parsed);
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  const upgradeTiers: PaidTier[] = notice ? paidTiersAbove(notice.tier) : [];

  const managePlan = () => {
    setNotice(null);
    navigate(settingsTabPath("billing"));
  };

  return (
    <AlertDialog open={notice !== null} onOpenChange={(open) => !open && setNotice(null)}>
      {/* Wider than the stock card, with a wrap guard: this is the one dialog
          with more than two footer actions (four for a free account: dismiss
          plus one upgrade per paid tier), whose no-wrap row otherwise inflates the
          grid track past the card (grid items default to min-width auto) and
          pushes both the copy and the footer band over the rounded corner.
          The width override mirrors the base class's data-[size] variant so
          tailwind-merge actually replaces it. */}
      <AlertDialogContent
        data-testid="units-exhausted-dialog"
        className="data-[size=default]:sm:max-w-md"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            You're out of AI usage for this {notice?.allowancePeriod ?? "month"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {notice && describe(notice, upgradeTiers.length > 0)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:flex-wrap">
          <AlertDialogCancel data-statsig-name="units-exhausted-dismiss">Not now</AlertDialogCancel>
          {notice?.tier === "free" ? (
            // No live subscription exists at the free tier, so checkout is the
            // direct route — monthly, the plan's default; annual billing is a
            // choice made on the Billing panel, where plan changes and their
            // proration confirmation also live.
            upgradeTiers.map((tier) => (
              <AlertDialogAction
                key={tier}
                disabled={startCheckout.isPending}
                onClick={(e) => {
                  // Keep the dialog up while checkout redirects, so a failed
                  // start leaves the flow on screen with its toast rather
                  // than vanishing both.
                  e.preventDefault();
                  startCheckout.mutate(
                    { tier, interval: "month" },
                    {
                      onError: (err) => {
                        // A past-due subscriber resolves to the free tier (no
                        // entitlement) while their subscription is still live,
                        // so checkout always answers already-subscribed. Route
                        // them to Billing — the surface that can actually fix
                        // the card or change the plan — instead of stranding
                        // them behind a button that can never succeed.
                        if (isAlreadySubscribed(err)) managePlan();
                      },
                    },
                  );
                }}
                data-statsig-name={`units-exhausted-upgrade-${tier}`}
              >
                Upgrade to {PLAN_TIER_LABELS[tier]}
              </AlertDialogAction>
            ))
          ) : (
            <AlertDialogAction onClick={managePlan} data-statsig-name="units-exhausted-manage-plan">
              {upgradeTiers.length > 0 ? "See upgrade options" : "Manage your plan"}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
